import { locationApi } from '../api/axios';
import type { DocumentClient } from './documents';

/* Mission telle que location-service la renvoie à son client propriétaire
   (ProjectionQuete.pourLeClient) : aucune donnée nominative de Spotter, et la
   synthèse « spotters » (nombre mobilisé, répartition par niveau). */

export interface RepartitionNiveau { niveau: number; nom: string; nombre: number }
export interface SyntheseSpotters { mobilises: number; parNiveau: RepartitionNiveau[] }

/** Trace serveur de la signature du devis (location-service, QuestEntity.signatureDevis). */
export interface SignatureDevisServeur {
  devisId?: number | null;
  devisNumero: string;
  montantTtc?: number | null;
  conditionsAcceptees: boolean;
  /** Horodatage du serveur, ISO 8601. */
  signeLe: string;
  adresseIp?: string | null;
  signataire?: string | null;
}

export interface ChampMission { id?: string; nom?: string; label?: string; type?: string }

export interface Mission {
  _id?: string;
  id?: string;
  titre: string;
  description?: string;
  statut: string;
  source?: string;
  statutPaiement?: string | null;
  types?: string[];
  champs?: ChampMission[];
  champsCollecte?: ChampMission[];
  frequence?: string;
  dateDebut?: string;
  dateFin?: string;
  collectesAttendues?: number;
  collectesRealisees?: number;
  budgetClient?: number;
  zone?: { coordinates?: number[][] };
  latMin?: number; latMax?: number; lngMin?: number; lngMax?: number;
  cycleActif?: boolean;
  createdAt?: string;
  updatedAt?: string;
  spotters?: SyntheseSpotters | null;
  signatureDevis?: SignatureDevisServeur | null;
}

export const idMission = (m: Mission): string => m._id || m.id || '';

export async function chargerMissions(email: string): Promise<Mission[]> {
  const res = await locationApi.get('/quest/byClient', { params: { email, _t: Date.now() } });
  const data = res.data;
  return Array.isArray(data) ? data : data?.data || [];
}

export const estEnCollecte = (m: Mission) => m.statut === 'publiee' || m.statut === 'active';
export const estLivree = (m: Mission) => m.statut === 'terminee';
export const estALEtude = (m: Mission) => m.statut === 'en_attente_validation' || m.statut === 'brouillon';

export function avancement(m: Mission): number {
  const attendues = m.collectesAttendues ?? 0;
  const faites = m.collectesRealisees ?? 0;
  return attendues > 0 ? Math.min(100, Math.round((faites / attendues) * 100)) : 0;
}

/** Date de livraison approchée : dernière mise à jour, sinon fin prévue. */
export const dateLivraison = (m: Mission) => m.updatedAt || m.dateFin || m.createdAt || '';

/* ── Devis à signer ───────────────────────────────────────────────────────
   La signature porte sur la mission (location-service, PATCH
   /quest/{id}/paiement → devis_signe). Un devis est « à signer » quand il
   est émis et que la mission n'a pas encore dépassé l'étape de signature. */
export const STATUTS_AVANT_SIGNATURE = [null, undefined, '', 'devis_envoye'];

export const missionSignee = (m: Mission | undefined) =>
  !!m && !STATUTS_AVANT_SIGNATURE.includes(m.statutPaiement as any);

/** Dernier devis émis de chaque mission non signée. */
export function devisASigner(documents: DocumentClient[], missions: Mission[]): DocumentClient[] {
  const parId = new Map(missions.map(m => [idMission(m), m]));
  const derniers = new Map<string, DocumentClient>();
  for (const d of documents) {
    if (d.type !== 'DEVIS' || !d.questId) continue;
    const prec = derniers.get(d.questId);
    if (!prec || (d.emisLe || '') > (prec.emisLe || '')) derniers.set(d.questId, d);
  }
  return Array.from(derniers.values()).filter(d => {
    const m = parId.get(d.questId as string);
    return m != null && !missionSignee(m);
  });
}
